import React, { useState, useEffect } from 'react';
import { showToast } from '../utils/toast';
import { useNavigate } from 'react-router-dom';
import ChatbotComponent from '../components/ChatbotComponent';
import BookmarkButton from '../components/BookmarkButton';
import '../styles/marketing-suite.css';
import { useRef, useCallback, memo, useMemo } from 'react';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
// Calculate activities count (including frequencies)

function calculateActivitiesCount(activities) {
  return activities.reduce((acc, act) => {
    return acc + (act.frequency || 1);
  }, 0);
}

// Format key-value pairs for display
function formatKeyValuePairs(keyValueData, maxPairs = 5) {
  if (!keyValueData || !Array.isArray(keyValueData)) {
    return [];
  }
  
  return keyValueData.slice(0, maxPairs).map(pair => ({
    key: pair.key || 'N/A',
    value: pair.value || 'N/A'
  }));
}

// Memoized search input component to prevent re-renders
const SearchInput = memo(({ value, onChange, placeholder }) => {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid rgba(0, 191, 174, 0.2)',
        fontSize: '14px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        outline: 'none',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#00bfae';
        e.target.style.boxShadow = '0 0 0 3px rgba(0, 191, 174, 0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = 'rgba(0, 191, 174, 0.2)';
        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
      }}
      onKeyDown={(e) => {
        // Prevent form submission on Enter key
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      autoComplete="off"
      spellCheck="false"
    />
  );
});

// Memoized filter modal component to isolate search input from main component re-renders
const FilterModal = memo(({ 
  isOpen, 
  onClose, 
  filterType, 
  setFilterType, 
  filterCategory, 
  setFilterCategory, 
  filterStatus, 
  setFilterStatus, 
  filterGrowthSuite, 
  setFilterGrowthSuite, 
  searchQuery, 
  setSearchQuery,
  onClearFilters,
  categories 
}) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(8px)'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.95) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '0',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)'
      }}>
        {/* Header with close button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 0 24px',
          position: 'relative',
          zIndex: 10
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#1a3c34',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{
              width: '32px',
              height: '32px',
              backgroundColor: '#00bfae',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: 'white'
            }}>
              🎯
            </span>
            Filter Activities
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Subtitle */}
        <p style={{
          fontSize: '14px',
          color: '#666',
          margin: '8px 24px 24px',
          fontWeight: 500
        }}>
          Customize your activity view with advanced filters
        </p>

        {/* Filter Content */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          margin: '0 24px 24px',
          borderRadius: '16px',
          padding: '24px',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
          maxHeight: '50vh',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Activity Type Filter */}
            <div>
              <label style={{
                display: 'block',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#6f42c1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  🏷️
                </span>
                Activity Type
              </label>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 191, 174, 0.2)',
                  fontSize: '14px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#00bfae';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 191, 174, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 191, 174, 0.2)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <option value="">All Types</option>
                <option value="plan_based">Plan Based</option>
                <option value="individual_brand">Individual Brand</option>
                <option value="shared">Shared</option>
              </select>
            </div>

            {/* Activity Category Filter */}
            <div>
              <label style={{
                display: 'block',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#17a2b8',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  📂
                </span>
                Activity Category
              </label>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 191, 174, 0.2)',
                  fontSize: '14px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#00bfae';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 191, 174, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 191, 174, 0.2)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Activity Status Filter */}
            <div>
              <label style={{
                display: 'block',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  🟢
                </span>
                Activity Status
              </label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 191, 174, 0.2)',
                  fontSize: '14px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#00bfae';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 191, 174, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 191, 174, 0.2)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            {/* Growth Suite Filter */}
            <div>
              <label style={{
                display: 'block',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#ffc107',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  ⭐
                </span>
                Growth Suite
              </label>
              <select
                value={filterGrowthSuite}
                onChange={e => setFilterGrowthSuite(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 191, 174, 0.2)',
                  fontSize: '14px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#00bfae';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 191, 174, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(0, 191, 174, 0.2)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <option value="">All Activities</option>
                <option value="true">Growth Suite Only</option>
                <option value="false">Regular Activities Only</option>
              </select>
            </div>

            {/* Search Filter */}
            <div>
              <label style={{
                display: 'block',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#dc3545',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  🔍
                </span>
                Search Activities
              </label>
              <SearchInput
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or description..."
              />
            </div>

            {/* Current Filter Summary */}
            <div style={{
              backgroundColor: 'rgba(0, 191, 174, 0.05)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(0, 191, 174, 0.1)',
              marginTop: '8px'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>📊</span> Active Filters
              </h4>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <div>Type: <strong>{filterType || 'All Types'}</strong></div>
                <div>Category: <strong>{filterCategory || 'All Categories'}</strong></div>
                <div>Status: <strong>{filterStatus === 'true' ? 'Active' : filterStatus === 'false' ? 'Inactive' : 'All Status'}</strong></div>
                <div>Growth Suite: <strong>{filterGrowthSuite === 'true' ? 'Growth Suite Only' : filterGrowthSuite === 'false' ? 'Regular Activities Only' : 'All Activities'}</strong></div>
                {searchQuery && <div>Search: <strong>"{searchQuery}"</strong></div>}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '0 24px 24px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClearFilters}
            style={{
              backgroundColor: 'rgba(108, 117, 125, 0.1)',
              color: '#6c757d',
              border: '1px solid rgba(108, 117, 125, 0.2)',
              borderRadius: '12px',
              padding: '12px 20px',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(108, 117, 125, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(108, 117, 125, 0.1)';
            }}
          >
            Clear Filters
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #00bfae 0%, #076e72 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 191, 174, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 16px rgba(0, 191, 174, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 191, 174, 0.3)';
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
});

// Placeholder: import or define your payment gateway integration function
// import { initiatePayment } from '../utils/payment';
function initiatePayment(payment) {
  // payment: { payment_url, payment_data }
  console.log('[PayU] initiatePayment called with:', payment);
  if (!payment || !payment.payment_url || !payment.payment_data) {
    showToast({ type: 'error', message: 'Payment gateway data missing.' });
    return;
  }
  // Create and auto-submit a form to PayU
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = payment.payment_url;
  form.target = '_self'; // Ensure navigation in same tab
  Object.entries(payment.payment_data).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  console.log('[PayU] Form created and appended:', form, form.action, form.method, form.target);
  // Use setTimeout to avoid React event interference
  setTimeout(() => {
    console.log('[PayU] Submitting form now...');
    try {
      form.submit();
      console.log('[PayU] Form submitted.');
    } catch (err) {
      console.error('[PayU] Form submit error:', err);
    }
    // Do not remove the form after submit, let browser handle navigation
  }, 0);
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function MarketingSuiteAppUser() {
  const navigate = useNavigate();

  // API state
  const [activities, setActivities] = useState({ available: [], locked: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showGrowthSuiteModal, setShowGrowthSuiteModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showLockUpgradeModal, setShowLockUpgradeModal] = useState(false);
  const [showActivityDetailsModal, setShowActivityDetailsModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Claimed activities state
  const [claimedActivities, setClaimedActivities] = useState([]);

  // Dedupe recently executed by activity_id, keep latest execution
  const recentlyExecutedActivities = useMemo(() => {
    try {
      const executed = (claimedActivities || []).filter(c => c.status === 'completed' || c.status === 'cancelled');
      const latestByActivity = new Map();
      executed.forEach(c => {
        const activityId = c.activity_id || c.activity?.id;
        if (!activityId) return;
        const prev = latestByActivity.get(activityId);
        const currDate = c.execution_date ? new Date(c.execution_date) : null;
        const prevDate = prev && prev.execution_date ? new Date(prev.execution_date) : null;
        if (!prev || (currDate && (!prevDate || currDate > prevDate))) {
          latestByActivity.set(activityId, c);
        }
      });
      const arr = Array.from(latestByActivity.values());
      arr.sort((a, b) => new Date(b.execution_date) - new Date(a.execution_date));
      return arr;
    } catch (_e) {
      return [];
    }
  }, [claimedActivities]);

  // Build KPIs with executions for a given activity from claimedActivities
  const buildKpisWithExecutionsForActivity = useCallback((activityId) => {
    try {
      const claimsForActivity = (claimedActivities || []).filter(c => (c.activity_id || c.activity?.id) === activityId);
      const kpiIdToKpi = new Map();

      claimsForActivity.forEach(claim => {
        const claimId = claim.claim_id || claim.id;
        const executionDate = claim.execution_date;
        const status = claim.status;

        (claim.kpis || []).forEach(k => {
          const kpiId = k.id || k.kpi_id;
          if (!kpiId) return;

          if (!kpiIdToKpi.has(kpiId)) {
            kpiIdToKpi.set(kpiId, {
              id: kpiId,
              name: k.name || k.kpi,
              description: k.description || '',
              measurement_unit: k.measurement_unit,
              input_type: k.input_type,
              options: k.options,
              is_required: k.is_required,
              executions: []
            });
          }

          const kpiEntry = kpiIdToKpi.get(kpiId);
          kpiEntry.executions.push({
            claim_id: claimId,
            execution_date: executionDate,
            status: status,
            value: k.value
          });
        });
      });

      // Sort executions (most recent first)
      kpiIdToKpi.forEach(kpi => {
        kpi.executions.sort((a, b) => new Date(b.execution_date) - new Date(a.execution_date));
      });

      return Array.from(kpiIdToKpi.values());
    } catch (err) {
      return [];
    }
  }, [claimedActivities]);

  // Filter state
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterGrowthSuite, setFilterGrowthSuite] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);

  // Brand and package state
  const [brand, setBrand] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  
  // Categories state
  const [categories, setCategories] = useState([]);
  // Claim activity handler
  const [claiming, setClaiming] = useState(false);
  const [claimDate, setClaimDate] = useState('2025-07-20');

  // Growth Suite state
  const [growthSuiteActivities, setGrowthSuiteActivities] = useState([]);
  const [growthSuiteLoading, setGrowthSuiteLoading] = useState(false);
  const [growthSuiteError, setGrowthSuiteError] = useState(null);
  const [purchasingId, setPurchasingId] = useState(null);
  const [availableCredits, setAvailableCredits] = useState(null);
  // Fix: store execution dates for each activity in a parent state object
  const [gsDates, setGsDates] = useState({});

  // Responsive state
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Note: fetchClaimedActivities functionality has been merged into fetchActivities

  // Use custom debounce hook for search
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // Fetch activities with filter and pagination (moved outside useEffect for reuse)
  const fetchActivities = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        
        // Use the new filtered paginated endpoint
        let url = `${API_BASE_URL}/ms/activities/filtered-paginated/`;
        const params = [];
        
        // Add filters
        if (filterType) params.push(`activity_type=${encodeURIComponent(filterType)}`);
        if (filterCategory) params.push(`category_id=${encodeURIComponent(filterCategory)}`);
        if (filterStatus !== '') params.push(`is_active=${filterStatus}`);
        if (filterGrowthSuite !== '') params.push(`is_growth_suite_activity=${filterGrowthSuite}`);
        if (debouncedSearchQuery.trim()) params.push(`search=${encodeURIComponent(debouncedSearchQuery.trim())}`);
        
        // Add pagination
        params.push(`limit=${pageSize}`);
        params.push(`offset=${currentPage * pageSize}`);
        
        // Include claimed activities and their KPIs
        params.push(`include_claimed=true`);
        params.push(`include_claimed_kpis=true`);
        
        if (params.length) url += '?' + params.join('&');
        
        const res = await fetch(url, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('API Error:', res.status, errorText);
          throw new Error(`Failed to fetch activities: ${res.status}`);
        }
        
        const data = await res.json();
        
        // Debug: Log the response message
        if (data.message) {
          console.log('API Response Message:', data.message);
        }
        
        if (data.status === 'success') {
          // Store total count for pagination
          setTotalCount(data.total_count || 0);
          
          // Handle the case where activities is an object with available/locked arrays
          let available = [];
          let locked = [];
          
          if (Array.isArray(data.activities)) {
            // If activities is a direct array (legacy format) - transform and categorize
            const activitiesArray = data.activities;
            const transformedActivities = activitiesArray.map(activity => ({
              id: activity.id,
              name: activity.name,
              description: activity.description,
              category: activity.category,
              type: activity.type,
              plan_eligibility: activity.plan_eligibility,
              frequency: activity.frequency,
              execution_duration: activity.execution_duration,
              activity_duration: activity.activity_duration,
              activity_threshold: activity.activity_threshold,
              half_yearly_flag: activity.half_yearly_flag,
              is_active: activity.is_active,
              growth_suite_price: activity.growth_suite_price,
              is_growth_suite_activity: activity.is_growth_suite_activity,
              created_at: activity.created_at,
              updated_at: activity.updated_at,
              tags: activity.tags,
              kpis: activity.kpis,
              used_claims: activity.used_claims || 0,
              remaining_claims: activity.remaining_claims || 0,
              pending_claims: activity.pending_claims || 0,
              claimable: activity.claimable || false,
              is_flagged: activity.is_flagged || false,
              locked: !activity.is_active || !activity.claimable
            }));
            
            available = transformedActivities.filter(activity => !activity.locked);
            locked = transformedActivities.filter(activity => activity.locked);
            
          } else if (data.activities && typeof data.activities === 'object') {
            // If activities is an object with available/locked arrays (new format) - trust backend categorization
            const transformActivity = (activity) => ({
              id: activity.id,
              name: activity.name,
              description: activity.description,
              category: activity.category,
              type: activity.type,
              plan_eligibility: activity.plan_eligibility,
              frequency: activity.frequency,
              execution_duration: activity.execution_duration,
              activity_duration: activity.activity_duration,
              activity_threshold: activity.activity_threshold,
              half_yearly_flag: activity.half_yearly_flag,
              is_active: activity.is_active,
              growth_suite_price: activity.growth_suite_price,
              is_growth_suite_activity: activity.is_growth_suite_activity,
              created_at: activity.created_at,
              updated_at: activity.updated_at,
              tags: activity.tags,
              kpis: activity.kpis,
              used_claims: activity.used_claims || 0,
              remaining_claims: activity.remaining_claims || 0,
              pending_claims: activity.pending_claims || 0,
              claimable: activity.claimable || false,
              is_flagged: activity.is_flagged || false,
              locked: activity.status === 'locked' // Use backend status for locked determination
            });
            
            available = (data.activities.available || []).map(transformActivity);
            locked = (data.activities.locked || []).map(transformActivity);
          }
          
        setActivities({
            available: available,
            locked: locked
          });
          
          // Extract and set claimed activities from the response
          console.log('Claimed activities from API:', data.claimed_activities);
          if (data.claimed_activities) {
            setClaimedActivities(data.claimed_activities);
          } else {
            setClaimedActivities([]);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch activities');
        }
      } catch (e) {
        setError(e.message);
        // Fallback to empty arrays if API fails
        setActivities({
          available: [],
          locked: []
        });
        setClaimedActivities([]);
      } finally {
        setLoading(false);
      }
  };

  // Fetch brand and package details
  const fetchBrandAndPackage = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/auth/users/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch user details');
        const data = await res.json();
        // New: get available credits from nested structure
        setAvailableCredits(data?.user?.brand?.available_credits ?? null);
        setBrand(data.user?.brand || null);
        setPackageDetails(data.package || null);
      } catch (e) {
        setBrand(null);
        setPackageDetails(null);
        setAvailableCredits(null);
      }
  };

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/ms/categories/`);
      if (!res.ok) throw new Error('Failed to fetch categories');
      const data = await res.json();
      if (data.status === 'success') {
        setCategories(data.categories || []);
      }
    } catch (e) {
      console.error('Error fetching categories:', e);
      setCategories([]);
    }
  };

  // Fetch activities with filter and pagination
  useEffect(() => {
    fetchActivities();
    fetchBrandAndPackage();
    fetchCategories();
  }, [filterType, filterCategory, filterStatus, filterGrowthSuite, debouncedSearchQuery, currentPage, pageSize]);

  // Ensure currentPage is within valid bounds when totalCount changes
  useEffect(() => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages > 0 && currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalCount, pageSize, currentPage]);

  // Tabs state
  const [tab, setTab] = useState('all');

  // Filtered lists
  const allActivities = [
    ...activities.available.map(a => ({ ...a, locked: false })),
    ...activities.locked.map(a => ({ ...a, locked: true }))
  ];
  const flaggedActivities = allActivities.filter(a => a.is_flagged);
  const lockedActivities = activities.locked.map(a => ({ ...a, locked: true }));

  // Handlers
  const handleClaim = (act) => {
    setSelectedActivity(act);
    setShowClaimModal(true);
  };
  const handleGrowthSuite = () => {
    setShowGrowthSuiteModal(true);
    fetchGrowthSuiteActivities();
  };
  const handleFilter = () => setShowFilterModal(true);
  const handleChatMessage = (message) => {
    // TODO: Implement chatbot functionality
    console.log('Chat message:', message);
  };

  // Flag/unflag API handlers
  const handleFlagToggle = async (act, flag) => {
    const token = localStorage.getItem('token');
    const url = flag
      ? `${API_BASE_URL}/ms/activity-unflag/`
      : `${API_BASE_URL}/ms/activity-flag/`;
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activity_id: act.id }),
      });
      setActivities(prev => {
        const updateFlag = a => a.id === act.id ? { ...a, is_flagged: !flag } : a;
        return {
          available: prev.available.map(updateFlag),
          locked: prev.locked.map(updateFlag),
        };
      });
    } catch (e) {
      alert('Failed to update flag status');
    }
  };

  // Claim submission handler
  const handleSubmitClaim = async () => {
    if (!selectedActivity || !claimDate) return;
    setClaiming(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/ms/activity-claims/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          activity_id: selectedActivity.id,
          execution_date: claimDate,
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        showToast({ type: 'success', message: data.message || 'Activity claimed successfully!' });
        setShowClaimModal(false);
        // Refresh activities after successful claim
        await fetchActivities();
      } else {
        showToast({ type: 'error', message: data.message || data.reason || 'Failed to claim activity.' });
      }
    } catch (e) {
      showToast({ type: 'error', message: 'Failed to claim activity.' });
    } finally {
      setClaiming(false);
    }
  };

  // Fetch Growth Suite activities
  const fetchGrowthSuiteActivities = async () => {
    setGrowthSuiteLoading(true);
    setGrowthSuiteError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/ms/growth-suite-activities/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch growth suite activities');
      const data = await res.json();
      // Fix: handle { activities: [...] } response
      setGrowthSuiteActivities(Array.isArray(data.activities) ? data.activities : []);
    } catch (e) {
      setGrowthSuiteError(e.message);
      setGrowthSuiteActivities([]);
    } finally {
      setGrowthSuiteLoading(false);
    }
  };

  // Purchase Growth Suite activity
  const handlePurchaseGrowthSuite = async (activityId) => {
    setPurchasingId(activityId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/ms/growth-suite-purchase/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activity_id: activityId }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast({ type: 'success', message: data.message || 'Growth Suite activity purchased!' });
        fetchGrowthSuiteActivities();
      } else {
        showToast({ type: 'error', message: data.message || data.reason || 'Failed to purchase activity.' });
      }
    } catch (e) {
      showToast({ type: 'error', message: 'Failed to purchase activity.' });
    } finally {
      setPurchasingId(null);
    }
  };

  // Simple modal component
  const Modal = ({ open, onClose, title, children, width, height }) => {
    if (!open) return null;
    const isLargeModal = width === '85%'; // Detect Growth Suite modal
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.4)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 24,
          padding: 32,
          width: width || '45%',
          minWidth: isLargeModal ? '600px' : '400px',
          maxWidth: isLargeModal ? '1200px' : '800px',
          maxHeight: isLargeModal ? '85vh' : '90vh',
          minHeight: isLargeModal ? '70vh' : 'auto',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          position: 'relative'
        }}>
          {title && (
            <>
              <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#00bfae' }}>×</button>
              <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1a3c34' }}>{title}</div>
            </>
          )}
          {children}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Marketing Suite Dashboard */}
      <div className="dashboard-container" style={{
        maxWidth: 'calc(100vw - 131px)', // Account for sidebar (111px) + some padding (20px)
        width: '100%',
        margin: '0 auto',
        // backgroundColor: '#ffffff',
        // borderRadius: '24px',
        overflow: 'hidden',
        // position: 'relative',
        // boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
      }}>
        {/* Background Decorations */}
        {/* <div className="background-decorations" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 0
        }}>
          <img src="/images/629_2040.svg" alt="background-blob" style={{position: 'absolute', filter: 'blur(150px)', opacity: 0.6, top: '-20%', left: '-15%', width: '70%'}} />
          <img src="/images/629_2042.svg" alt="background-blob" style={{position: 'absolute', filter: 'blur(150px)', opacity: 0.6, top: '-30%', left: '20%', width: '40%'}} />
          <img src="/images/629_2041.svg" alt="background-blob" style={{position: 'absolute', filter: 'blur(150px)', opacity: 0.6, top: '10%', right: '-20%', width: '50%'}} />
          <img src="/images/629_2056.svg" alt="background-blob" style={{position: 'absolute', filter: 'blur(150px)', opacity: 0.6, bottom: '-25%', left: '5%', width: '60%', transform: 'rotate(35deg)'}} />
        </div> */}

        {/* Dashboard Body */}
        <div className="dashboard-body" style={{ position: 'relative', zIndex: 1 }}>
          {/* Main Content */}
          <main className="main-content" style={{
            padding: '3rem',
            background: 'transparent',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {/* Main Header */}
            <header className="main-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: windowWidth > 768 ? 'center' : 'flex-start',
              flexDirection: windowWidth > 768 ? 'row' : 'column',
              gap: windowWidth > 768 ? '0' : '20px'
            }}>
              <div className="welcome-text">
                <h1 style={{ fontSize: windowWidth > 768 ? '36px' : '28px', fontWeight: 700, color: '#000000' }}>Welcome, {brand?.name || 'MonkStory'}!</h1>
                <p style={{ fontSize: windowWidth > 768 ? '26px' : '18px', color: '#1b1d1e', marginTop: '8px' }}>Your Brand's Marketing Journey - All in One Place</p>
              </div>
              <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* <button style={{width: '70px', height: '70px', borderRadius: '50%', backgroundColor: '#ffffff', border: '1px solid #d7d7d7', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <img src="/images/629_2177.svg" alt="Messages" />
                </button>
                <button style={{width: '70px', height: '70px', borderRadius: '50%', backgroundColor: '#ffffff', border: '1px solid #d7d7d7', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <img src="/images/629_2184.svg" alt="Notifications" />
                </button>
                <button style={{width: '70px', height: '70px', borderRadius: '50%', backgroundColor: '#ffffff', border: '1px solid #d7d7d7', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <img src="/images/monk-story-icon.png" alt="User Avatar" style={{width: '55px', height: '55px', borderRadius: '50%', objectFit: 'cover'}} />
                </button> */}
              </div>
            </header>

            {/* Content Layout */}
            <div className="content-layout" style={{
              display: 'grid',
              gridTemplateColumns: windowWidth > 1440 ? '3fr 1fr' : windowWidth > 1200 ? '2.5fr 1fr' : '1fr',
              gap: '30px',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              {/* Primary Column */}
              <div className="primary-column" style={{ width: '100%', minWidth: 0, overflow: 'visible', padding: '0 8px' }}>
                {/* Growth Banner */}
                <section className="growth-banner" style={{
                  backgroundImage: 'url("/images/growth-suite-bg.png")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '48px',
                  padding: '40px',
                  color: '#ffffff',
                  position: 'relative',
                  overflow: 'visible',
                  marginBottom: '30px',
                  // marginTop: '36px',
                  height: '14rem',
                  cursor: 'pointer',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start'
                }} onClick={handleGrowthSuite}>
                  <div className="" style={{
                    position: 'absolute',
                    top: '25%',
                  }}>
                    <span style={{ fontSize: '26px', fontWeight: 700, marginBottom: '8px', color: '#ffffff', display: 'block', marginTop: '1rem' }}>Need More Growth This Term??</span>
                    <p style={{ fontSize: '15.6px', fontWeight: 500, opacity: 0.9, marginBottom: '24px', color: '#ffffff', margin: '8px 0 24px 0' }}>Unlock Extra Visibility & Reach - Instantly</p>
                    <button style={{
                      backgroundColor: '#0b4e51',
                      color: '#ffffff',
                      padding: '10px 22px',
                      borderRadius: '26px',
                      fontWeight: 700,
                      fontSize: '14px',
                      display: 'inline-block',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      border: 'none',
                      cursor: 'pointer'
                    }}>Explore Growth Suite</button>
                  </div>
                  {/* <img src="/images/plant-hand.png" alt="Hands holding a growing plant" style={{
                    position: 'absolute',
                    right: '40px',
                    bottom: '30px',
                    width: '315px',
                    height: 'auto',
                    transform: 'translateY(48px)'
                  }} /> */}
                </section>

                {/* Marketing Suite */}
                <section className="marketing-suite" style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '48px',
                  padding: '40px',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  margin: '0 -8px'
                }}>
                  {/* Suite Header */}
                  <header className="suite-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '30px',
                    color: '#ffffff'
                  }}>
                    <div className="suite-title">
                      <h2 style={{ fontSize: '32px', fontWeight: 500, color: '#fff' }}>Marketing Suite</h2>
                      <p style={{ fontSize: '24px', marginTop: '8px' }}>Your Marketing Activities for this Plan</p>
                    </div>
                    <button className="sort-by-button" style={{
                      border: '1px solid #949494',
                      borderRadius: '360px',
                      padding: '16px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#00000',
                      fontSize: '16px',
                      cursor: 'pointer'
                    }} onClick={handleFilter}>
                      <span>Filter</span>
                      <img src="/images/629_2355.svg" alt="sort icon" style={{ transform: 'rotate(-90deg)' }} />
                    </button>
                  </header>

                  {/* Suite Tabs */}
                  <nav className="suite-tabs">
                    <ul style={{
                      display: 'flex',
                      gap: '30px',
                      borderBottom: '3px solid #d9d9d9',
                      marginBottom: '30px'
                    }}>
                      <li>
                        <button
                          className={`tab-item ${tab === 'all' ? 'active' : ''}`}
                          style={{
                            paddingBottom: '16px',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '22px',
                            fontWeight: tab === 'all' ? 700 : 500,
                            color: tab === 'all' ? '#076e72' : '#504f4f',
                            position: 'relative',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            borderBottom: tab === 'all' ? '3px solid #076e72' : 'none'
                          }}
                          onClick={() => setTab('all')}
                        >
                          All <span style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            padding: '4px 12px',
                            borderRadius: '20px',
                            backgroundColor: '#ceedef',
                            color: '#076e72'
                          }}>{calculateActivitiesCount(allActivities)}</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`tab-item ${tab === 'flagged' ? 'active' : ''}`}
                          style={{
                            paddingBottom: '16px',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '22px',
                            fontWeight: tab === 'flagged' ? 700 : 500,
                            color: tab === 'flagged' ? '#076e72' : '#504f4f',
                            position: 'relative',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            borderBottom: tab === 'flagged' ? '3px solid #076e72' : 'none'
                          }}
                          onClick={() => setTab('flagged')}
                        >
                          Flagged <span style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            padding: '4px 12px',
                            borderRadius: '20px',
                            backgroundColor: '#fcdfdf',
                            color: '#3d2222'
                          }}>{calculateActivitiesCount(flaggedActivities)}</span>
                        </button>
                      </li>
                      <li>
                        <button
                          className={`tab-item ${tab === 'locked' ? 'active' : ''}`}
                          style={{
                            paddingBottom: '16px',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '22px',
                            fontWeight: tab === 'locked' ? 700 : 500,
                            color: tab === 'locked' ? '#076e72' : '#504f4f',
                            position: 'relative',
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            borderBottom: tab === 'locked' ? '3px solid #076e72' : 'none'
                          }}
                          onClick={() => setTab('locked')}
                        >
                          Locked <span style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            padding: '4px 12px',
                            borderRadius: '20px',
                            backgroundColor: '#fcdfdf',
                            color: '#3d2222'
                          }}>{calculateActivitiesCount(lockedActivities)}</span>
                        </button>
                      </li>
                    </ul>
                  </nav>

                  {/* Suite Cards */}
                  {loading ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: '#fff' }}>Loading...</div>
                  ) : error ? (
                    <div style={{ color: '#c00', padding: '32px' }}>{error}</div>
                  ) : (
                    <div className="suite-cards" style={{
                      display: 'grid',
                      gridTemplateColumns: windowWidth > 1440 ? 'repeat(2, 1fr)' : '1fr',
                      gap: '24px',
                      width: '100%',
                      maxWidth: '100%'
                    }}>
                      {(tab === 'all' ? allActivities : tab === 'flagged' ? flaggedActivities : tab === 'locked' ? lockedActivities : []).map((act, index) => {
                        const maxNameLen = 22;
                        const isLongName = act.name.length > maxNameLen;
                        const displayName = isLongName ? act.name.slice(0, maxNameLen) + '…' : act.name;
                        const isShared = act.type === 'shared';

                        // Get appropriate icon and color based on activity status
                        const getActivityIcon = () => {
                          if (act.locked) return '/images/activity-icons/Locked icon.svg';
                          if (act.claimable && !isShared) return '/images/activity-icons/Available icon.svg';
                          if (act.status === 'pending' || act.pending_claims > 0) return '/images/activity-icons/Pending icon.svg';
                          if (act.status === 'in_progress') return '/images/activity-icons/In progress icon.svg';
                          if (isShared) return '/images/activity-icons/Shared icon.svg';
                          return '/images/activity-icons/Executed icon.svg';
                        };

                        // Get icon color based on status
                        const getIconColor = () => {
                          if (act.locked) return '#ae508e'; // Purple for locked
                          if (act.is_flagged) return '#387cbb'; // Blue for flagged
                          if (act.claimable && !isShared) return '#0d6f3e'; // Green for available
                          if (act.status === 'pending' || act.pending_claims > 0) return '#f57c00'; // Orange for pending
                          if (act.status === 'in_progress') return '#00bfae'; // Teal for in progress
                          if (isShared) return '#6f42c1'; // Purple for shared
                          return '#218910'; // Green for executed
                        };

                        const getIconWrapperColor = () => {
                          const colors = ['#e0e9ff', '#fff8e0', '#f1e0ff', '#e0ffeb'];
                          return colors[index % colors.length];
                        };

                        const getStatusTag = () => {
                          if (act.locked) return {
                            text: 'Locked',
                            bg: '#fdd7f0',
                            color: '#ae508e',
                            border: '#ae508e',
                            icon: '/images/activity-icons/Locked icon.svg'
                          };
                          if (act.is_flagged) return {
                            text: '1x Flagged',
                            bg: '#d1e9ff',
                            color: '#387cbb',
                            border: '#387cbb',
                            icon: '/images/activity-icons/Flagged icon.svg'
                          };
                          if (act.claimable && !isShared) return {
                            text: '✓ Available',
                            bg: '#cafce3',
                            color: '#0d6f3e',
                            border: '#78c09c',
                            icon: '/images/activity-icons/Available icon.svg'
                          };
                          // Use backend claim data to show execution status
                          const usedCount = act.used_claims ?? 0;
                          if (usedCount > 0) {
                          return {
                              text: `${usedCount}x Executed`,
                            bg: '#daffd4',
                            color: '#218910',
                            border: '#46b833',
                            icon: '/images/activity-icons/Executed icon.svg'
                            };
                          }
                          return {
                            text: '✓ Available',
                            bg: '#cafce3',
                            color: '#0d6f3e',
                            border: '#78c09c',
                            icon: '/images/activity-icons/Available icon.svg'
                          };
                        };

                        const statusTag = getStatusTag();
                        const usedCount = act.used_claims ?? 0;
                        const pendingCount = act.pending_claims ?? 0;
                        const remainingCount = act.remaining_claims ?? 0;
                        const totalCount = usedCount + pendingCount + remainingCount;

                        return (
                          <div key={act.id} className="activity-card" style={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #d7d7d7',
                            borderRadius: '33px',
                            padding: windowWidth > 768 ? '24px' : '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            opacity: act.locked ? 0.7 : 1,
                            cursor: 'pointer',
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                            onClick={() => {
                              // Ensure consistent activity structure for modal
                              const mappedActivity = {
                                ...act,
                                activity_name: act.name,
                                activity_description: act.description,
                                activity_category: act.category,
                                activity_type: act.type,
                                activity_duration: act.activity_duration,
                                execution_date: act.execution_date,
                                execution_start_date: act.execution_start_date,
                                created_at: act.created_at,
                                kpis: act.kpis || []
                              };
                              setSelectedActivity(mappedActivity);
                              setShowClaimModal(false);
                              setShowGrowthSuiteModal(false);
                              setShowFilterModal(false);
                              setShowActivityDetailsModal(false);
                              if (act.locked) {
                                setTimeout(() => setShowLockUpgradeModal(true), 0);
                              } else {
                                setTimeout(() => setShowActivityDetailsModal(true), 0);
                              }
                            }}>
                            <BookmarkButton
                              isFlagged={act.is_flagged}
                              onClick={() => handleFlagToggle(act, act.is_flagged)}
                              title={act.is_flagged ? 'Remove from saved' : 'Save activity'}
                            />

                            {/* Card Header */}
                            <div className="card-header" style={{
                              display: windowWidth > 640 ? 'grid' : 'flex',
                              gridTemplateColumns: windowWidth > 640 ? 'auto 1fr auto' : 'none',
                              flexDirection: windowWidth > 640 ? 'row' : 'column',
                              alignItems: windowWidth > 640 ? 'center' : 'flex-start',
                              gap: '12px',
                              width: '100%'
                            }}>
                              <div style={{
                                width: windowWidth > 640 ? '61px' : '48px',
                                height: windowWidth > 640 ? '61px' : '48px',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: getIconWrapperColor(),
                                flexShrink: 0
                              }}>
                                <img src={getActivityIcon()} alt="activity icon" style={{
                                  width: windowWidth > 640 ? '32px' : '24px',
                                  height: windowWidth > 640 ? '32px' : '24px',
                                  filter: `brightness(0) saturate(100%) invert(1) sepia(1) saturate(10000%) hue-rotate(${(() => {
                                    const color = getIconColor();
                                    if (color === '#ae508e') return '280deg'; // Purple for locked
                                    if (color === '#387cbb') return '210deg'; // Blue for flagged
                                    if (color === '#0d6f3e') return '120deg'; // Green for available
                                    if (color === '#f57c00') return '30deg'; // Orange for pending
                                    if (color === '#00bfae') return '180deg'; // Teal for in progress
                                    if (color === '#6f42c1') return '270deg'; // Purple for shared
                                    return '100deg'; // Green for executed
                                  })()})`
                                }} />
                              </div>
                              <h3 style={{
                                fontSize: windowWidth > 640 ? '20px' : '18px',
                                fontWeight: 500,
                                flex: 1,
                                minWidth: 0,
                                wordBreak: 'break-word'
                              }} title={act.name}>{displayName}</h3>
                              <span style={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '14.6px',
                                fontWeight: 500,
                                padding: '4px 12px',
                                borderRadius: '5px',
                                border: `1px solid ${statusTag.border}`,
                                backgroundColor: statusTag.bg,
                                color: statusTag.color,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <img src={statusTag.icon} alt="status" style={{
                                  width: '14px',
                                  height: '14px',
                                  filter: `brightness(0) saturate(100%) invert(1) sepia(1) saturate(10000%) hue-rotate(${(() => {
                                    if (statusTag.color === '#ae508e') return '280deg'; // Purple for locked
                                    if (statusTag.color === '#387cbb') return '210deg'; // Blue for flagged
                                    if (statusTag.color === '#0d6f3e') return '120deg'; // Green for available
                                    if (statusTag.color === '#218910') return '100deg'; // Green for executed
                                    return '0deg'; // Default
                                  })()})`
                                }} />
                                {statusTag.text}
                              </span>
                            </div>

                            {/* Card Progress */}
                            <div className="card-progress">
                              <div style={{
                                width: '100%',
                                height: '9px',
                                backgroundColor: '#f0eded',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                marginBottom: '8px',
                                position: 'relative'
                              }}>
                                {/* Used claims (red) */}
                                <div style={{
                                  height: '100%',
                                  backgroundColor: '#f87171',
                                  borderRadius: '6px',
                                  width: totalCount > 0 ? `${(usedCount / totalCount) * 100}%` : '0%',
                                  position: 'absolute',
                                  left: 0,
                                  zIndex: 2
                                }}></div>
                                {/* Pending claims (yellow) */}
                                <div style={{
                                  height: '100%',
                                  backgroundColor: '#fbbf24',
                                  borderRadius: '6px',
                                  width: totalCount > 0 ? `${(pendingCount / totalCount) * 100}%` : '0%',
                                  position: 'absolute',
                                  left: totalCount > 0 ? `${(usedCount / totalCount) * 100}%` : '0%',
                                  zIndex: 1
                                }}></div>
                              </div>
                              <p style={{ fontSize: '15.7px', color: '#5f5757' }}>
                                {usedCount} used, {pendingCount} pending, {remainingCount} remaining
                              </p>
                            </div>

                            {/* Card Actions */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: windowWidth > 640 ? 'nowrap' : 'wrap',
                              gap: '8px',
                              width: '100%'
                            }}>
                              <div style={{
                                gap: '8px',
                                display: 'flex',
                              }}>
                                {!act.locked && act.claimable && !isShared && (
                                  <button
                                    style={{
                                      backgroundColor: '#0b4e51',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: windowWidth > 640 ? '7px 20px' : '6px 16px',
                                      fontWeight: 600,
                                      fontSize: windowWidth > 640 ? '15px' : '14px',
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap'
                                    }}
                                    onClick={(e) => { e.stopPropagation(); handleClaim(act); }}
                                  >
                                    Claim
                                  </button>
                                )}
                              </div>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedActivity(act);
                                  setShowActivityDetailsModal(true);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ba4720',
                                  fontWeight: 500,
                                  fontSize: windowWidth > 640 ? '15.7px' : '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Know More <img src="/images/red-upmoving-arrow.svg" alt="arrow" style={{
                                  width: windowWidth > 640 ? '16px' : '14px'
                                }} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Pagination Controls */}
                  {totalCount > pageSize && (() => {
                    const totalPages = Math.ceil(totalCount / pageSize);
                    const isFirstPage = currentPage === 0;
                    const isLastPage = currentPage >= totalPages - 1;
                    
                    return (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '40px',
                      padding: '24px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '16px',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <button
                        onClick={() => !isFirstPage && setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={isFirstPage}
                        style={{
                          padding: '12px 20px',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          backgroundColor: isFirstPage ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                          color: isFirstPage ? 'rgba(26, 60, 52, 0.5)' : '#1a3c34',
                          cursor: isFirstPage ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 600,
                          transition: 'all 0.2s ease',
                          opacity: isFirstPage ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!isFirstPage) {
                            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.color = '#ffffff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isFirstPage) {
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.color = '#1a3c34';
                          }
                        }}
                      >
                        ← Previous
                      </button>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{
                          color: '#1a3c34',
                          fontSize: '14px',
                          fontWeight: 500
                        }}>
                          Page
                        </span>
                        <span style={{
                          color: '#00bfae',
                          fontSize: '16px',
                          fontWeight: 700,
                          padding: '8px 12px',
                          backgroundColor: 'rgba(0, 191, 174, 0.1)',
                          borderRadius: '8px',
                          border: '1px solid rgba(0, 191, 174, 0.3)'
                        }}>
                          {currentPage + 1}
                        </span>
                        <span style={{
                          color: '#1a3c34',
                          fontSize: '14px',
                          fontWeight: 500
                        }}>
                          of {Math.ceil(totalCount / pageSize)}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => !isLastPage && setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                        disabled={isLastPage}
                        style={{
                          padding: '12px 20px',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          backgroundColor: isLastPage ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                          color: isLastPage ? 'rgba(26, 60, 52, 0.5)' : '#1a3c34',
                          cursor: isLastPage ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 600,
                          transition: 'all 0.2s ease',
                          opacity: isLastPage ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!isLastPage) {
                            e.target.style.backgroundColor = 'rgba(26, 24, 24, 0.3)';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.color = '#ffffff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isLastPage) {
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.color = '#1a3c34';
                          }
                        }}
                      >
                        Next →
                      </button>
                    </div>
                    );
                  })()}
                </section>
              </div>

              {/* Secondary Column */}
              <aside className="secondary-column" style={{
                display: windowWidth > 1200 ? 'flex' : 'none',
                flexDirection: 'column',
                gap: '30px',
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
                marginTop: '40px'
              }}>
                {/* Activities Panel */}
                <section className="activities-panel" style={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '40px',
                  padding: '32px',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                  position: 'relative'
                }}>
                  <header className="panel-header" style={{ marginBottom: '24px' }}>
                    <h2 style={{ fontSize: '22px', fontWeight: 500 }}>Your Activities</h2>
                    {/* <p style={{fontSize: '22px', color: '#535960', marginTop: '4px'}}>Keep Track Of Your Tasks</p> */}
                  </header>

                  {/* Upcoming Activities */}
                  <div className="activity-group" style={{ marginBottom: '24px' }}>
                    <div className="activity-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <h3 style={{ fontSize: '22px', fontWeight: 500 }}>Upcoming Activities</h3>
                      <a href="#"><img src="/images/I629_2480_2_10106.svg" alt="arrow" style={{ width: '16px' }} /></a>
                    </div>
                    <div className="activity-list">
                      {claimedActivities.filter(claim => claim.status === 'approved').length === 0 ? (
                        <div style={{ color: '#888', fontSize: '14px', padding: '12px 0', borderTop: '1px solid #e6e6e6' }}>No upcoming activities</div>
                      ) : claimedActivities.filter(claim => claim.status === 'approved').map((claim) => {
                        const maxNameLen = 22;
                        const isLongName = (claim.activity_name || claim.activity?.name || '').length > maxNameLen;
                        const displayName = isLongName ? (claim.activity_name || claim.activity?.name || '').slice(0, maxNameLen) + '…' : (claim.activity_name || claim.activity?.name || '');
                        const execDate = claim.execution_date || claim.activity?.execution_date || '';
                        const day = execDate ? new Date(execDate).getDate() : '';

                        return (
                          <div key={claim.claim_id || claim.id} className="activity-item" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '16px 20px',
                            marginBottom: '12px',
                            borderRadius: '20px',
                            border: 'none',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }} onClick={() => {
                            // Map claim data to match activity structure for consistency
                            const mappedActivity = {
                              ...claim,
                              id: claim.activity_id,
                              name: claim.activity_name,
                              description: claim.activity_description,
                              category: claim.activity_category,
                              type: claim.activity_type,
                              activity_duration: claim.activity_duration,
                              execution_date: claim.execution_date,
                              execution_start_date: claim.execution_start_date,
                              created_at: claim.created_at,
                              kpis: buildKpisWithExecutionsForActivity(claim.activity_id)
                            };
                            setSelectedActivity(mappedActivity);
                            setShowActivityDetailsModal(true);
                          }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              fontSize: '18px',
                              fontWeight: 700,
                              flexShrink: 0,
                              backgroundColor: '#7CB342',
                              color: '#ffffff',
                              boxShadow: '0 2px 6px rgba(124, 179, 66, 0.2)'
                            }}>
                              {day}
                            </div>
                            <div className="activity-details" style={{ flex: 1, overflow: 'hidden' }}>
                              <h4 style={{ fontSize: '17px', fontWeight: 600, marginBottom: '4px', color: '#000' }}>{displayName}</h4>
                              <p style={{ fontSize: '14px', color: '#666', opacity: 0.8 }}>{execDate}</p>
                            </div>
                            <div className="activity-arrow" style={{
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 12L10 8L6 4" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    

                  </div>

                  {/* Recently Executed */}
                  <div className="activity-group" style={{ marginBottom: '24px' }}>
                    <div className="activity-header" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <h3 style={{ fontSize: '22px', fontWeight: 500 }}>Recently Executed</h3>
                      <a href="#"><img src="/images/I629_2481_2_10106.svg" alt="arrow" style={{ width: '16px' }} /></a>
                    </div>
                    <div className="activity-list">
                      {recentlyExecutedActivities.length === 0 ? (
                        <div style={{ color: '#888', fontSize: '14px', padding: '12px 0', borderTop: '1px solid #e6e6e6' }}>No recently executed activities</div>
                      ) : recentlyExecutedActivities.map((claim) => {
                        const maxNameLen = 22;
                        const isLongName = (claim.activity_name || claim.activity?.name || '').length > maxNameLen;
                        const displayName = isLongName ? (claim.activity_name || claim.activity?.name || '').slice(0, maxNameLen) + '…' : (claim.activity_name || claim.activity?.name || '');
                        const execDate = claim.execution_date || claim.activity?.execution_date || '';
                        const day = execDate ? new Date(execDate).getDate() : '';

                        return (
                          <div key={claim.claim_id || claim.id} className="activity-item" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '12px 0',
                            borderTop: '1px solid #e6e6e6',
                            cursor: 'pointer'
                          }} onClick={() => {
                            // Map claim data to match activity structure for consistency
                            const mappedActivity = {
                              ...claim,
                              id: claim.activity_id,
                              name: claim.activity_name,
                              description: claim.activity_description,
                              category: claim.activity_category,
                              type: claim.activity_type,
                              activity_duration: claim.activity_duration,
                              execution_date: claim.execution_date,
                              execution_start_date: claim.execution_start_date,
                              created_at: claim.created_at,
                              kpis: buildKpisWithExecutionsForActivity(claim.activity_id)
                            };
                            setSelectedActivity(mappedActivity);
                            setShowActivityDetailsModal(true);
                          }}>
                            <div style={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '10px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexShrink: 0,
                              backgroundColor: '#daffd4',
                              border: '1px solid #46b833'
                            }}>
                              <img src="/images/activity-icons/Executed icon.svg" alt="executed" style={{
                                width: '24px',
                                height: '24px',
                                filter: 'brightness(0) saturate(100%) invert(1) sepia(1) saturate(10000%) hue-rotate(100deg)'
                              }} />
                            </div>
                            <div className="activity-details">
                              <h4 style={{ fontSize: '18px', fontWeight: 500 }}>{displayName}</h4>
                              <p style={{ fontSize: '16px', color: '#7f7f7f', marginTop: '4px' }}>{execDate}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>

                {/* Chatbot Panel */}
                <ChatbotComponent
                  onMessageSend={handleChatMessage}
                  avatarSrc="/images/tara-avatar.png"
                  sendIconSrc="/images/chat-send-icon.png"
                />
              </aside>
            </div>
          </main>
        </div>
      </div>

      {/* Popups/Modals - Keep all existing modals */}
      <Modal open={showClaimModal} onClose={() => setShowClaimModal(false)} title="" width="40%">
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '0',
          margin: '-32px',
          minHeight: '400px',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)'
        }}>
          {/* Header with close button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 0 24px',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#1a3c34',
              margin: 0
            }}>
              Claim Activity
            </h2>
            <button
              onClick={() => setShowClaimModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Activity Info */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            margin: '24px',
            borderRadius: '16px',
            padding: '20px',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#1a3c34',
              marginBottom: '16px'
            }}>
              {selectedActivity?.name}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#17a2b8',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  📂
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: '#333' }}>Category:</span>
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                    {selectedActivity?.category}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#6f42c1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white'
                }}>
                  🏷️
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: '#333' }}>Type:</span>
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                    {selectedActivity?.type}
                  </span>
                </div>
              </div>
            </div>

            {/* Date Selection */}
            <div style={{
              backgroundColor: 'rgba(0, 191, 174, 0.05)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(0, 191, 174, 0.1)',
              marginBottom: '20px'
            }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '8px',
                fontSize: '16px'
              }}>
                Select execution date:
              </label>
              <input
                type="date"
                value={claimDate}
                onChange={e => setClaimDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e6f4f1',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  outline: 'none'
                }}
              />
            </div>

            {/* Submit Button */}
            <button
              style={{
                width: '100%',
                background: claiming ? '#6c757d' : 'linear-gradient(135deg, #00bfae 0%, #076e72 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 24px',
                fontWeight: 600,
                fontSize: '16px',
                cursor: claiming ? 'not-allowed' : 'pointer',
                opacity: claiming ? 0.7 : 1,
                boxShadow: claiming ? 'none' : '0 4px 12px rgba(0, 191, 174, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onClick={handleSubmitClaim}
              disabled={claiming}
            >
              {claiming ? 'Submitting...' : 'Submit Claim'}
            </button>
          </div>
        </div>
      </Modal>
      <Modal open={showGrowthSuiteModal} onClose={() => setShowGrowthSuiteModal(false)} title="Explore Growth Suite" width="85%">
        <div style={{ display: 'flex', justifyContent: "space-between" }}>
          <div style={{ marginBottom: 18, fontWeight: 600, color: '#00bfae', fontSize: 16 }}>
            Available Credits: <span style={{ color: '#1a3c34', fontWeight: 700 }}>{availableCredits !== null ? availableCredits : '--'} credits</span>
          </div>
          <div style={{ marginBottom: 18, fontWeight: 600, color: '#00bfae', fontSize: 16, cursor: 'pointer' }}>
            <span onClick={() => navigate(`/wallet?brand_id=${brand.id}`)} style={{ textDecoration: 'underline' }}>Buy Credits</span>
          </div>
        </div>
        <div style={{ marginBottom: 18, paddingBottom: 4 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
            minHeight: 200
          }}>
            {growthSuiteLoading ? (
              <div style={{ color: '#888', fontSize: 14, alignSelf: 'center' }}>Loading...</div>
            ) : growthSuiteError ? (
              <div style={{ color: '#c00', fontSize: 14, alignSelf: 'center' }}>{growthSuiteError}</div>
            ) : growthSuiteActivities.length === 0 ? (
              <div style={{ color: '#888', fontSize: 14, alignSelf: 'center' }}>No growth suite activities available.</div>
            ) : (
              // Sort: claimable=true first, then others
              [...growthSuiteActivities].sort((a, b) => {
                if (a.claimable === b.claimable) return 0;
                return a.claimable ? -1 : 1;
              }).map(act => {
                // FIX: use parent state for execution date
                const gsDate = gsDates[act.id] || '';
                return (
                  <div key={act.id} style={{
                    background: '#f8fcfb',
                    border: '1px solid #e6f4f1',
                    borderRadius: 14,
                    padding: '20px',
                    width: '100%',
                    height: 'fit-content',
                    minHeight: '280px',
                    boxShadow: '0 2px 8px rgba(0, 191, 174, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    position: 'relative',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer'
                  }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 16px rgba(0, 191, 174, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 191, 174, 0.08)';
                    }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: 4
                    }}>
                      <img src="/images/activity-icons/Growth suite icon.svg" alt="growth suite" style={{
                        width: '20px',
                        height: '20px',
                        filter: 'brightness(0) saturate(100%) invert(1) sepia(1) saturate(10000%) hue-rotate(180deg)'
                      }} />
                      <div style={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: '#1a3c34',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 180
                      }} title={act.name}>{act.name}</div>
                    </div>
                    <div style={{ fontSize: 13, color: '#7a8b8a', marginBottom: 4 }}><b>Category:</b> {act.category || '-'}</div>
                    <div style={{ fontSize: 13, color: '#7a8b8a', marginBottom: 4 }}><b>Plan:</b> {act.plan_name || '-'}</div>
                    <div style={{ fontSize: 13, color: '#7a8b8a', marginBottom: 4 }}><b>Frequency:</b> {act.frequency ?? '-'}</div>
                    <div style={{ fontSize: 13, color: '#7a8b8a', marginBottom: 8 }}><b>Credits:</b> {act.growth_suite_price ?? '--'}</div>
                    {act.claimable && (
                      <div style={{ marginBottom: 8, width: '100%' }}>
                        <input type="date" value={gsDate} onChange={e => setGsDates(d => ({ ...d, [act.id]: e.target.value }))} style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #e6f4f1', fontSize: 13 }} min={new Date().toISOString().split('T')[0]} />
                      </div>
                    )}
                    {!act.claimable && (
                      <div style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }} title="Locked">
                        <img src="/images/activity-icons/Locked icon.svg" alt="locked" style={{
                          width: '24px',
                          height: '24px',
                          opacity: 0.6,
                          filter: 'brightness(0) saturate(100%) invert(1) sepia(1) saturate(10000%) hue-rotate(280deg)'
                        }} />
                      </div>
                    )}
                    <button
                      style={{ marginTop: 'auto', background: act.claimable ? '#00bfae' : '#e6f4f1', color: act.claimable ? '#fff' : '#aaa', border: 'none', borderRadius: 8, padding: '7px 20px', fontWeight: 600, fontSize: 15, cursor: act.claimable ? (purchasingId === act.id ? 'not-allowed' : 'pointer') : 'not-allowed', opacity: purchasingId === act.id ? 0.7 : 1, width: '100%', maxWidth: 220, transition: 'background 0.2s' }}
                      disabled={!act.claimable || purchasingId === act.id}
                      onClick={async () => {
                        if (!act.claimable) return;
                        if (!gsDate) {
                          showToast({ type: 'error', message: 'Please select an execution date.' });
                          return;
                        }
                        setPurchasingId(act.id);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_BASE_URL}/ms/growth-suite-purchase-v2/`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ activity_id: act.id, execution_date: gsDate }),
                          });
                          const data = await res.json();
                          if (res.ok && data.status === 'success') {
                            showToast({ type: 'success', message: data.message || 'Growth Suite activity purchased!' });
                            fetchGrowthSuiteActivities();
                            // Refetch user info for updated credits
                            const userRes = await fetch(`${API_BASE_URL}/api/auth/users/`, { headers: { 'Authorization': `Bearer ${token}` } });
                            if (userRes.ok) {
                              const userData = await userRes.json();
                              setAvailableCredits(userData?.user?.brand?.available_credits ?? null);
                            }
                          } else if (data.status === 'payment_required') {
                            showToast({ type: 'info', message: 'Insufficient credits. Please buy more credits.' });
                            navigate(`/wallet?brand_id=${brand.id}`);
                          } else {
                            showToast({ type: 'error', message: data.message || data.reason || 'Failed to purchase activity.' });
                          }
                        } catch (e) {
                          showToast({ type: 'error', message: 'Failed to purchase activity.' });
                        } finally {
                          setPurchasingId(null);
                        }
                      }}
                    >{purchasingId === act.id ? 'Processing...' : act.claimable ? 'Claim' : 'Locked'}</button>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#888' }}>If you don't have enough credits, you will be prompted to pay via payment gateway.</div>
      </Modal>
      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filterType={filterType}
        setFilterType={setFilterType}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterGrowthSuite={filterGrowthSuite}
        setFilterGrowthSuite={setFilterGrowthSuite}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        categories={categories}
        onClearFilters={() => {
                setFilterType('');
                setFilterCategory('');
          setFilterStatus('');
          setFilterGrowthSuite('');
          setSearchQuery('');
          setCurrentPage(0);
        }}
      />
      {/* Add locked upgrade modal */}
      <Modal open={showLockUpgradeModal} onClose={() => setShowLockUpgradeModal(false)} title="Upgrade Required">
        <div style={{ marginBottom: 16 }}>
          <b>{selectedActivity?.name}</b><br />
          <div style={{ margin: '12px 0' }}>This activity is locked. Upgrade to the <b>{selectedActivity?.plan_name || selectedActivity?.type || 'required plan'}</b> plan to unlock and use this activity.</div>
        </div>
        <button style={{ background: '#00bfae', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>View Plans</button>
      </Modal>

      {/* Activity Details Modal */}
      <Modal open={showActivityDetailsModal} onClose={() => setShowActivityDetailsModal(false)} title="" width="50%">
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 248, 255, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '0',
          margin: '-32px',
          minHeight: '600px',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: 'none'
        }}>
          {/* Header with close button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 0 24px',
            position: 'relative',
            zIndex: 10
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#1a3c34',
              margin: 0
            }}>
              {selectedActivity?.activity_name || selectedActivity?.name || 'Activity Details'}
            </h2>
            <button
              onClick={() => setShowActivityDetailsModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Subtitle */}
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: '8px 24px 24px',
            fontWeight: 500
          }}>
            {selectedActivity?.activity_category || selectedActivity?.category || 'Marketing'} activity - {selectedActivity?.activity_type || selectedActivity?.type || 'Campaign'}
          </p>

          {/* Activity Details Section */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              margin: '0 24px 20px',
              borderRadius: '16px',
              padding: '20px',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
            }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1a3c34',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#00bfae',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: 'white'
              }}>
                ℹ
              </span>
              Activity Details
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#6c757d',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: 'white'
                }}>
                  📅
                </span>
                <div>
                  <span style={{ fontWeight: 600, color: '#333' }}>Date:</span>
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                    {selectedActivity?.execution_date
                      ? `Execution Date: ${selectedActivity.execution_date}`
                      : selectedActivity?.execution_start_date
                        ? `Start Date: ${selectedActivity.execution_start_date}`
                        : '*Will be scheduled once activity is claimed.'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#17a2b8',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: 'white',
                  marginTop: '2px'
                }}>
                  📝
                </span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: '4px' }}>Description:</span>
                  <p style={{
                    fontSize: '14px',
                    color: '#666',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {selectedActivity?.activity_description || selectedActivity?.description ||
                      'A comprehensive marketing activity designed to boost your brand visibility and engagement. This activity will be executed by our expert team according to your brand guidelines and marketing objectives.'}
                  </p>
                </div>
              </div>

              {/* Activity Tags */}
              {selectedActivity?.tags && selectedActivity.tags.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#6f42c1',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white',
                    marginTop: '2px'
                  }}>
                    🏷️
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>Tags:</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {selectedActivity.tags.map(tag => (
                        <span key={tag.id || tag.name} style={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          borderRadius: '12px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid #bbdefb'
                        }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>


          {/* Multiple Deployment History Sections - One for each execution */}
          {(() => {
            // Get all executions from KPIs
            const allExecutions = [];
            const dynamicKPIs = selectedActivity?.kpis || [];
            
            // Collect all unique executions from all KPIs
            dynamicKPIs.forEach(kpi => {
              if (kpi.executions && Array.isArray(kpi.executions)) {
                kpi.executions.forEach(execution => {
                  // Check if this execution is already added
                  const existingExecution = allExecutions.find(ex => 
                    ex.execution_date === execution.execution_date && 
                    ex.claim_id === execution.claim_id
                  );
                  if (!existingExecution) {
                    allExecutions.push(execution);
                  }
                });
              }
            });
            
            // Sort executions by date (most recent first)
            allExecutions.sort((a, b) => new Date(b.execution_date) - new Date(a.execution_date));
            
            // If no executions found, show a single section with current activity data
            if (allExecutions.length === 0) {
              return (
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              margin: '0 24px 20px',
              borderRadius: '16px',
              padding: '20px',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#1a3c34',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#6f42c1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: 'white'
                }}>
                  📊
                </span>
                Deployment History
              </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#6c757d',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white'
                      }}>
                        📅
                      </span>
                      <div>
                        <span style={{ fontWeight: 600, color: '#333' }}>Created:</span>
                        <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                          {selectedActivity?.created_at
                            ? new Date(selectedActivity.created_at).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#17a2b8',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white'
                      }}>
                        ⚡
                      </span>
                      <div>
                        <span style={{ fontWeight: 600, color: '#333' }}>Duration:</span>
                        <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                          {selectedActivity?.activity_duration || 'N/A'} days
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#dc3545',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white'
                      }}>
                        📈
                      </span>
                      <div>
                        <span style={{ fontWeight: 600, color: '#333' }}>Analytics:</span>
                        <span style={{ marginLeft: '8px', fontSize: '14px', color: '#888' }}>
                          *Data is aggregated from Meta dashboard
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            
            // Show multiple deployment history sections for each execution
            return allExecutions.map((execution, executionIndex) => {
              // Get KPIs for this specific execution - show all KPIs with their values
              const executionKPIs = dynamicKPIs.map(kpi => {
                if (kpi.executions && Array.isArray(kpi.executions)) {
                  const executionKpi = kpi.executions.find(exec => 
                    exec.execution_date === execution.execution_date && 
                    exec.claim_id === execution.claim_id
                  );
                  if (executionKpi) {
                    return {
                      ...kpi,
                      value: executionKpi.value,
                      latest_value: executionKpi.value
                    };
                  } else {
                    // If no specific execution found, show the KPI with null value
                    return {
                      ...kpi,
                      value: null,
                      latest_value: null
                    };
                  }
                }
                return {
                  ...kpi,
                  value: null,
                  latest_value: null
                };
              });
              
              return (
                <div key={`${execution.claim_id}-${execution.execution_date}`} style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  margin: '0 24px 20px',
                  borderRadius: '16px',
                  padding: '20px',
                  backdropFilter: 'blur(15px)',
                  WebkitBackdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1a3c34',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#6f42c1',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white'
                    }}>
                      📊
                    </span>
                    Deployment History - Execution {executionIndex + 1}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: (() => {
                          if (execution.status === 'completed') return '#28a745';
                          if (execution.status === 'pending') return '#ffc107';
                          if (execution.status === 'cancelled') return '#dc3545';
                      return '#6c757d';
                    })(),
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white'
                  }}>
                    {(() => {
                          if (execution.status === 'completed') return '✓';
                          if (execution.status === 'pending') return '⏳';
                          if (execution.status === 'cancelled') return '✕';
                      return '?';
                    })()}
                  </span>
                  <div>
                    <span style={{ fontWeight: 600, color: '#333' }}>Status:</span>
                    <span style={{
                      marginLeft: '8px',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: (() => {
                            if (execution.status === 'completed') return '#d4edda';
                            if (execution.status === 'pending') return '#fff3cd';
                            if (execution.status === 'cancelled') return '#f8d7da';
                        return '#e2e3e5';
                      })(),
                      color: (() => {
                            if (execution.status === 'completed') return '#155724';
                            if (execution.status === 'pending') return '#856404';
                            if (execution.status === 'cancelled') return '#721c24';
                        return '#6c757d';
                      })(),
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {(() => {
                            if (execution.status === 'completed') return 'Completed';
                            if (execution.status === 'pending') return 'Pending';
                            if (execution.status === 'cancelled') return 'Cancelled';
                        return 'Unknown';
                      })()}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#6c757d',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white'
                  }}>
                    📅
                  </span>
                  <div>
                        <span style={{ fontWeight: 600, color: '#333' }}>Execution Date:</span>
                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                          {new Date(execution.execution_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#17a2b8',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white'
                  }}>
                    ⚡
                  </span>
                  <div>
                    <span style={{ fontWeight: 600, color: '#333' }}>Duration:</span>
                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                      {selectedActivity?.activity_duration || 'N/A'} days
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#dc3545',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white'
                  }}>
                    📈
                  </span>
                  <div>
                    <span style={{ fontWeight: 600, color: '#333' }}>Analytics:</span>
                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#888' }}>
                      *Data is aggregated from Meta dashboard
                    </span>
                  </div>
                </div>

                    {/* KPI Metrics for this specific execution */}
                    {executionKPIs.length > 0 && (
                      <>
                        <div style={{ marginTop: '20px', marginBottom: '16px' }}>
                          <h4 style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#1a3c34',
                            marginBottom: '12px',
                  display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: '#28a745',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              color: 'white'
                            }}>
                              📊
                            </span>
                            KPI Metrics - {selectedActivity?.activity_category || selectedActivity?.category || 'Performance Marketing'}
                          </h4>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '12px',
                            marginTop: '16px'
                          }}>
                            {executionKPIs.map((kpi, index) => (
                              <div key={index} style={{
                  backgroundColor: 'rgba(0, 191, 174, 0.05)',
                                borderRadius: '12px',
                                padding: '16px',
                                border: '1px solid rgba(0, 191, 174, 0.1)',
                                textAlign: 'center',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 8px 25px rgba(0, 191, 174, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }}>
                                <div style={{
                                  fontSize: '24px',
                                  fontWeight: 700,
                                  color: '#00bfae',
                                  marginBottom: '4px'
                                }}>
                                  {(() => {
                                    const value = kpi.value;
                                    const name = kpi.name || kpi.kpi;
                                    const valueJson = kpi.value_json;
                                    
                                    // If value_json exists and has key-value pairs, display them in green boxes
                                    if (valueJson && Array.isArray(valueJson) && valueJson.length > 0) {
                                      return (
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                          {valueJson.map((pair, index) => (
                                            <div key={index} style={{ display: 'flex', gap: '4px' }}>
                                              <div style={{
                                                backgroundColor: 'rgba(0, 191, 174, 0.05)',
                                                borderRadius: '12px',
                                                padding: '8px 12px',
                                                border: '1px solid rgba(0, 191, 174, 0.1)',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#00bfae'
                                              }}>
                                                {pair.key}
                                              </div>
                                              <div style={{
                                                backgroundColor: 'rgba(0, 191, 174, 0.05)',
                                                borderRadius: '12px',
                                                padding: '8px 12px',
                                                border: '1px solid rgba(0, 191, 174, 0.1)',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#1a3c34'
                                              }}>
                                                {pair.value}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    }
                                    
                                    // Fallback to regular value display
                                    if (value === null || value === undefined || value === '') {
                                      return 'N/A';
                                    }
                                    
                                    let displayValue = value;
                                    if (typeof value === 'number' && value >= 1000) {
                                      displayValue = (value / 1000).toFixed(1) + 'K';
                                    }
                                    
                                    // Add units based on KPI name
                                    if (name.includes('Rate') || name.includes('CTR')) {
                                      displayValue += '%';
                                    } else if (name.includes('CPC') || name.includes('CAC') || name.includes('EMV')) {
                                      displayValue = '₹' + displayValue;
                                    } else if (name.includes('ROAS')) {
                                      displayValue += 'x';
                                    }
                                    
                                    return displayValue;
                                  })()}
                  </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  fontWeight: 500,
                                  lineHeight: '1.3'
                                }}>
                                  {(() => {
                                    const name = kpi.name || kpi.kpi;
                                    const keyValuePairs = formatKeyValuePairs(kpi.options);
                                    
                                    // Debug: Log the options data to see what we're receiving
                                    if (kpi.options && typeof kpi.options === 'object') {
                                      console.log('KPI Options Debug:', {
                                        name: name,
                                        options: kpi.options,
                                        formatted: keyValuePairs
                                      });
                                    }
                                    
                                    if (keyValuePairs.length > 0) {
                                      return (
                                        <div>
                                          <div style={{ marginBottom: '4px', fontWeight: 600 }}>{name}</div>
                                          <div style={{ fontSize: '10px', color: '#999' }}>
                                            {keyValuePairs.map((pair, index) => (
                                              <span key={index} style={{ marginRight: '8px' }}>
                                                <strong>{pair.key}:</strong> {pair.value}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    // If options exist but couldn't be formatted, show them as key-value pairs
                                    if (kpi.options && typeof kpi.options === 'object' && !Array.isArray(kpi.options)) {
                                      const rawOptions = kpi.options;
                                      const optionPairs = Object.entries(rawOptions).slice(0, 5).map(([key, value]) => ({
                                        key: key,
                                        value: String(value)
                                      }));
                                      
                                      if (optionPairs.length > 0) {
                                        return (
                                          <div>
                                            <div style={{ marginBottom: '4px', fontWeight: 600 }}>{name}</div>
                                            <div style={{ fontSize: '10px', color: '#999' }}>
                                              {optionPairs.map((pair, index) => (
                                                <span key={index} style={{ marginRight: '8px' }}>
                                                  <strong>{pair.key}:</strong> {pair.value}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      }
                                    }
                                    
                                    // Check if the KPI value itself contains JSON data that needs to be parsed
                                    if (kpi.value && typeof kpi.value === 'string' && kpi.value.startsWith('{') && kpi.value.endsWith('}')) {
                                      try {
                                        const parsedValue = JSON.parse(kpi.value);
                                        if (typeof parsedValue === 'object' && parsedValue !== null) {
                                          const valuePairs = Object.entries(parsedValue).slice(0, 5).map(([key, value]) => ({
                                            key: key,
                                            value: String(value)
                                          }));
                                          
                                          if (valuePairs.length > 0) {
                                            return (
                                              <div>
                                                <div style={{ marginBottom: '4px', fontWeight: 600 }}>{name}</div>
                                                <div style={{ fontSize: '10px', color: '#999' }}>
                                                  {valuePairs.map((pair, index) => (
                                                    <span key={index} style={{ marginRight: '8px' }}>
                                                      <strong>{pair.key}:</strong> {pair.value}
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          }
                                        }
                                      } catch (e) {
                                        // If JSON parsing fails, continue with normal display
                                      }
                                    }
                                    
                                    return name;
                                  })()}
                  </div>
                  </div>
                            ))}
                </div>

                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: 'rgba(0, 191, 174, 0.1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 191, 174, 0.2)',
                            fontSize: '12px',
                            color: '#00695c',
                            textAlign: 'center'
                          }}>
                            📊 KPI metrics for execution on {new Date(execution.execution_date).toLocaleDateString()}
              </div>
            </div>
                      </>
          )}
                  </div>
                </div>
              );
            });
          })()}

          {/* Creative Approval Section - Only show for claimed activities */}
          {/* {(selectedActivity?.claim_id || selectedActivity?.status) && (
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            margin: '0 24px 24px',
            borderRadius: '16px',
            padding: '20px',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1a3c34',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#fd7e14',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: 'white'
              }}>
              🎨
              </span>
              Creative Approval Section
            </h3>
            
            <div style={{
              backgroundColor: '#fff5f5',
              border: '2px dashed #fed7d7',
              borderRadius: '12px',
              padding: '40px 20px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#fed7d7',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                fontSize: '20px'
              }}>
                📤
              </div>
              <p style={{
                color: '#dc3545',
                fontSize: '14px',
                fontWeight: 500,
                margin: 0
              }}>
                Creative not available yet. You'll be notified when it's ready for review.
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button style={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                border: '1px solid #bbdefb',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Request A Change
              </button>
              <button style={{
                backgroundColor: '#fff3e0',
                color: '#f57c00',
                border: '1px solid #ffcc02',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                Flag As Priority
              </button>
            </div>
          </div>
          )} */}
        </div>
      </Modal>
    </div>
  );
}